import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = `${window.location.origin}/api`;

function AuthCallback() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasProcessed = useRef(false);
  const [status, setStatus] = useState('Authenticating...');

  const clearStaleCookies = () => {
    document.cookie = 'session_token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'O_session_token=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Clear stale cookies before any auth attempt
    clearStaleCookies();

    const authToken = searchParams.get('auth_token');
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.substring(1));
    const sessionId = hashParams.get('session_id');

    if (authToken) {
      exchangeToken(authToken);
    } else if (sessionId) {
      // Try direct session exchange first (single-phase, faster)
      processSession(sessionId.trim());
    } else {
      navigate('/', { replace: true });
    }
  }, [navigate, searchParams]);

  const processSession = async (sessionId, retryCount = 0) => {
    try {
      setStatus('Verifying Google account...');
      // Single-phase: exchange session_id directly for a session cookie
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );

      const { user } = response.data;
      const returnUrl = localStorage.getItem('helmcore_return_url');
      if (returnUrl) {
        localStorage.removeItem('helmcore_return_url');
        navigate(returnUrl, { state: { user }, replace: true });
      } else {
        navigate('/dashboard', { state: { user }, replace: true });
      }
    } catch (error) {
      console.error(`Auth session exchange failed (attempt ${retryCount + 1}):`, error?.response?.data || error.message);

      if (retryCount < 2) {
        setStatus(`Retrying authentication (${retryCount + 1}/2)...`);
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return processSession(sessionId, retryCount + 1);
      }

      // If single-phase fails, try two-phase as fallback
      if (retryCount === 2) {
        return initToken(sessionId);
      }

      clearStaleCookies();
      setStatus('Authentication failed. Please try again.');
      setTimeout(() => navigate('/', { replace: true }), 2000);
    }
  };

  const initToken = async (sessionId, retryCount = 0) => {
    try {
      setStatus('Trying alternative authentication...');
      const response = await axios.post(`${API}/auth/init-token`, {
        session_id: sessionId
      });

      const { auth_token } = response.data;
      setStatus('Securing session...');
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      window.location.replace(`${window.location.origin}/auth/callback?auth_token=${auth_token}`);
    } catch (error) {
      console.error('Auth init-token failed:', error?.response?.data || error.message);
      if (retryCount < 1) {
        setTimeout(() => initToken(sessionId, retryCount + 1), 1500);
      } else {
        clearStaleCookies();
        setStatus('Authentication failed. Please try again.');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    }
  };

  const exchangeToken = async (authToken, retryCount = 0) => {
    try {
      setStatus('Setting up your session...');
      const response = await axios.post(
        `${API}/auth/exchange`,
        { auth_token: authToken },
        { withCredentials: true }
      );

      const { user } = response.data;
      const returnUrl = localStorage.getItem('helmcore_return_url');
      if (returnUrl) {
        localStorage.removeItem('helmcore_return_url');
        navigate(returnUrl, { state: { user }, replace: true });
      } else {
        navigate('/dashboard', { state: { user }, replace: true });
      }
    } catch (error) {
      console.error('Auth exchange failed:', error?.response?.data || error.message);
      if (retryCount < 1) {
        setStatus('Retrying...');
        setTimeout(() => exchangeToken(authToken, retryCount + 1), 1000);
      } else {
        clearStaleCookies();
        setStatus('Session setup failed. Please try again.');
        setTimeout(() => navigate('/', { replace: true }), 2000);
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="auth-callback-page">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-navy border-t-transparent"></div>
        <p className="mt-4 text-slate-600" data-testid="auth-status">{status}</p>
      </div>
    </div>
  );
}

export default AuthCallback;
